import { RefObject, useEffect, useState } from 'react';

/**
 * Hook to detect if text content overflows and doesn't fit in one line
 * Works even when ellipsis-text class is applied
 * 
 * @param ref - React ref to the text element
 * @returns boolean - true if text overflows, false otherwise
 */
export const useDetectTextOverflow = (ref: RefObject<HTMLElement | null>): boolean => {
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      setHasOverflow(false);
      return;
    }

    const checkOverflow = () => {
      // Compare scrollWidth with clientWidth to detect overflow
      // scrollWidth includes the full content width even if it's hidden by ellipsis
      const isOverflowing = element.scrollWidth > element.clientWidth;
      setHasOverflow(isOverflowing);
    };

    // Check on mount and when content changes
    checkOverflow();

    // Create ResizeObserver to handle dynamic changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(element);

    // Also listen to window resize
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [ref]);

  return hasOverflow;
};
